import type { CarePlan, Patient, Reference } from '@medplum/fhirtypes'
import { GoalCarePlan } from '@seen/core/models/care-plans/GoalCarePlan'
import { InitialCarePlan } from '@seen/core/models/care-plans/InitialCarePlan'
import { Intervention } from '@seen/core/models/care-plans/intervention'
import type { QuestionnaireWithResponse } from '@seen/core/models/questionnaireResponse'
import { CarePlanParticipantSignatureTask } from '@seen/core/models/tasks/signature/carePlanParticipantSignatureTask'
import { CarePlanSignatureTask } from '@seen/core/models/tasks/signature/carePlanSignatureTask'
import { sortBy } from 'lodash-es'
import { useMemo } from 'react'
import { useCarePlanDemographics } from '#src/hooks/useCarePlanDemographics.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

interface UseCarePlanDataResult {
  carePlan: InitialCarePlan | undefined
  goalCarePlans: GoalCarePlan[]
  signatures: CarePlanSignatureTask[]
  carePlanParticipantSignatures: CarePlanParticipantSignatureTask[]
  interventionsByGoalCarePlan: {
    goalCarePlan: GoalCarePlan
    interventions: Intervention[]
  }[]
  interventions: Intervention[]
  demographics: QuestionnaireWithResponse[]
  isLoading: boolean
  error: Error | null
}

/**
 * Custom hook to load all data required for the CarePlanDisplayPage and its child components.
 * This centralizes data fetching to avoid redundant API calls in nested components.
 * @param participant - The patient to load the care plan for.
 * @param carePlanReference - The reference of the care plan to load.
 * @returns An object containing all the loaded data.
 */
export function useCarePlanData(
  participant: Reference<Patient>,
  carePlanReference: Reference<CarePlan>
): UseCarePlanDataResult {
  const carePlanQuery = useRead(InitialCarePlan, carePlanReference)
  const carePlan = carePlanQuery.data

  const goalCarePlansQuery = useSearch(
    GoalCarePlan,
    GoalCarePlan.searchQuery({
      initialCarePlan: carePlanReference,
    })
  )

  const goalCarePlans = useMemo(
    () => sortBy(goalCarePlansQuery.data ?? [], (c) => c.sortOrder),
    [goalCarePlansQuery.data]
  )

  const signaturesQuery = useSearch(
    CarePlanSignatureTask,
    CarePlanSignatureTask.byCarePlanReference({ carePlan: carePlanReference })
  )

  const carePlanParticipantSignatureQuery = useSearch(
    CarePlanParticipantSignatureTask,
    CarePlanParticipantSignatureTask.byCarePlanReference({
      carePlan: carePlanReference,
    })
  )

  const interventionsQueries = useSearch(
    Intervention,
    Intervention.byInitialCarePlan({
      initialCarePlan: carePlanReference,
    })
  )

  const interventionsByGoalCarePlan = useMemo(() => {
    const result = new Map<string, Intervention[]>()

    for (const intervention of interventionsQueries.data ?? []) {
      const key = intervention.goalCarePlan?.reference
      if (key) {
        const existing = result.get(key) ?? []
        result.set(key, [...existing, intervention])
      }
    }

    // Sort interventions within each goal
    for (const [key, interventions] of result.entries()) {
      result.set(
        key,
        sortBy(interventions, (i) => i.sortOrder)
      )
    }

    return goalCarePlans.map((goalCarePlan) => ({
      goalCarePlan,
      interventions: result.get(goalCarePlan.reference.reference) ?? [],
    }))
  }, [goalCarePlans, interventionsQueries.data])

  const {
    demographics,
    isLoading: demographicsIsLoading,
    error: demographicsError,
  } = useCarePlanDemographics({
    patient: participant,
    carePlan: carePlanReference,
  })

  const isLoading =
    carePlanQuery.isLoading ||
    goalCarePlansQuery.isLoading ||
    signaturesQuery.isLoading ||
    carePlanParticipantSignatureQuery.isLoading ||
    interventionsQueries.isLoading ||
    demographicsIsLoading

  const error =
    carePlanQuery.error ??
    goalCarePlansQuery.error ??
    signaturesQuery.error ??
    carePlanParticipantSignatureQuery.error ??
    interventionsQueries.error ??
    demographicsError

  return {
    carePlan,
    goalCarePlans,
    signatures: signaturesQuery.data ?? [],
    carePlanParticipantSignatures: carePlanParticipantSignatureQuery.data ?? [],
    interventionsByGoalCarePlan,
    interventions: interventionsQueries.data ?? [],
    demographics,
    isLoading,
    error,
  }
}
